import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TenantHttpService } from '@src/app/core/http/tenant.http.service';
import { SessionService } from '@src/app/core/session.service';
import { TagFieldCustom } from '@src/app/shared/components/tag-field/tag-field.component';
import { AttributeType } from '@src/app/shared/enums/attribute-type.enum';
import { untilDestroyed } from '@src/app/shared/functions/until-destroyed';
import {
  AttributeDescription,
  AttributeValue,
} from '@src/app/shared/interfaces/attribute-description.interface';
import {
  Category,
  CategoryTag,
} from '@src/app/shared/interfaces/category.interface';
import { OrganizationInterface } from '@src/app/shared/interfaces/organization.interface';
import { TenantInterface } from '@src/app/shared/interfaces/tenant.interface';
import { FormUtils } from '@src/app/shared/utils/form-utils';

@Component({
  selector: 'app-category-settings',
  templateUrl: './category-settings.component.html',
})
export class CategorySettingsComponent implements OnChanges, OnDestroy {
  @Input() attributeDescription: AttributeDescription;
  @Input() entity: TenantInterface | OrganizationInterface;
  @Input() editable: boolean;
  @Input() isOrgCategorySettings = false;
  @Input() maxCategoryAmount = 3;
  @Input() isCreating: boolean = false;

  form: UntypedFormGroup;
  allType = AttributeType;
  MAX_ACTIVE_CATEGORIES_AMOUNT = 3;

  get activeCategory(): number {
    let categories;

    if (!this.isCreating && !this.editable && this.entity) {
      categories = this.entity.categories;
      return categories.filter((category) => category.isActivated).length;
    } else {
      categories = this.form?.value?.categories;
      return categories.filter((category) => category.IsActivated).length;
    }
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly tenantHttpService: TenantHttpService,
    private readonly sessionService: SessionService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxCategoryAmount?.currentValue) {
      this.createForm();
    }

    if (changes?.entity?.currentValue) {
      this.setFormValue(changes.entity.currentValue);
    }

    if (changes.editable) {
      const categoryArray = this.form?.get('categories') as FormArray;

      if (this.editable && categoryArray) {
        categoryArray?.controls.forEach((item) => item.enable());
      } else {
        categoryArray?.controls.forEach((item) => item.disable());
      }

      if (this.entity) {
        this.setFormValue(this.entity);
      }
    }

    if (
      changes?.isOrgCategorySettings?.currentValue &&
      changes?.isCreating?.currentValue
    ) {
      this.getTenantCategories()
        .pipe(untilDestroyed(this))
        .subscribe((categories: Category[]) => {
          const updatedCategories = categories.map((category) => {
            category.categoryTags.forEach((tag) => {
              tag.isTenantTag = true;
            });
            category.isTenantCategory = true;
            return category;
          });

          this.setFormValue({ categories: updatedCategories });
        });
    }
  }

  shouldDisableCheckbox(isChecked: boolean): boolean {
    return (
      !this.editable ||
      (!isChecked && this.activeCategory >= this.MAX_ACTIVE_CATEGORIES_AMOUNT)
    );
  }

  getTooltip(isChecked: boolean): string {
    if (!this.editable || isChecked) return '';
    if (this.activeCategory >= this.MAX_ACTIVE_CATEGORIES_AMOUNT)
      return 'UI.OrganizationCategory.IsActivatedTooltip';
  }

  shouldShowCategoryInViewMode(categoryForm): boolean {
    if (this.isCreating || this.editable) return true;

    const category = categoryForm.value;
    return (
      category?.CategoryName ||
      category?.CategoryTags?.length > 0 ||
      category?.IsActivated
    );
  }

  getTenantCategories() {
    const tenantId = this.sessionService.tenant.id;
    return this.tenantHttpService.getTenantCategories(tenantId);
  }

  getFormArrayItem(i: number): FormGroup {
    const categoryArray = this.form.get('categories') as FormArray;
    return categoryArray.at(i) as FormGroup;
  }

  createForm(): void {
    this.form = this.fb.group({
      categories: this.fb.array(
        [...Array(this.maxCategoryAmount)].map(() =>
          this.createCategoryControl()
        )
      ),
    });
  }

  createCategoryControl(): FormGroup {
    const formGroup = this.fb.group({
      Id: [null],
      CategoryName: [''],
      CategoryTags: [null],
      IsActivated: [false],
      IsTenantCategory: [false],
    });

    this.validateCategoryNameAndTags(formGroup);

    return formGroup;
  }

  setFormValue(entity): void {
    if (!entity || !this.form) return;
    const categories = entity.categories as Category[];
    if (!categories.length) return;
    const categoriesArray = this.form.get('categories') as FormArray;

    categories.forEach((category, index) => {
      const mappedTags = this.transformToTagModal(category.categoryTags);

      const value = {
        Id: category.id,
        CategoryName: category.name ?? '',
        CategoryTags: mappedTags ?? [],
        IsActivated: category.isActivated,
        IsTenantCategory: category.isTenantCategory,
      };

      categoriesArray.at(index).patchValue(value);
    });
  }

  setTagValue(value: any[], index: number): void {
    const categoriesArray = this.form.get('categories') as FormArray;
    const categoryGroup = categoriesArray.at(index) as FormGroup;
    const mappedValue = value.map((i) => ({
      ...i,
      isTenantTag: !!i.isTenantTag,
    }));
    categoryGroup.get('CategoryTags').setValue(mappedValue);
  }

  getFormValue() {
    return this.form.value?.categories;
  }

  getFieldOptions(key: string): AttributeDescription {
    return FormUtils.getFieldOptions(
      this.attributeDescription.relatedEntityAttributeDescriptions,
      key
    );
  }

  getFieldValue(
    attributeValues: AttributeValue[],
    fieldName: string
  ): AttributeValue {
    return FormUtils.getFieldValue(attributeValues, fieldName);
  }

  setCheckboxValue(value, i) {
    const categoriesArray = this.form.get('categories') as FormArray;
    categoriesArray.at(i)?.get('IsActivated')?.setValue(value);
  }

  private validateCategoryNameAndTags(formGroup: FormGroup): void {
    const categoryNameControl = formGroup.get('CategoryName');
    const categoryTagsControl = formGroup.get('CategoryTags');
    const isActivatedControl = formGroup.get('IsActivated');

    isActivatedControl.valueChanges.subscribe((isActivated) => {
      if (isActivated) {
        categoryNameControl.setValidators([Validators.required]);
        categoryTagsControl.setValidators([Validators.required]);
      } else {
        categoryNameControl.clearValidators();
        categoryTagsControl.clearValidators();
      }

      categoryNameControl.updateValueAndValidity();
      categoryTagsControl.updateValueAndValidity();
    });
  }

  transformToTagModal(items: CategoryTag[]): TagFieldCustom[] {
    if (items && Array.isArray(items)) {
      return items.map((item) => {
        return {
          display: item.content,
          value: item.content,
          readonly: false,
          id: item.id,
          ownerId: item.ownerId,
          disableTagEdit: item.isTenantTag,
          isTenantTag: item.isTenantTag,
        };
      });
    } else {
      return [];
    }
  }

  ngOnDestroy(): void {
    /** */
  }
}
